import React, { useState } from "react";
import {
  MessageSquare,
  TrendingUp,
  Users,
  BarChart3,
  Check,
} from "lucide-react";
import { TourStep } from "./types";
import { TourScreen } from "./tour";
import { useNavigate } from "react-router-dom";
import { hasCrmAccess, hasMarketingAccess } from "../../../utils/org-role";
import ConvoCrm from "../../../assets/images/convo-crm.png";
import ConvoMarketing from "../../../assets/images/convo-marketing.png";
import ConvoUnify from "../../../assets/images/convo-unify.png";
import ConvoTrack from "../../../assets/images/convo-track.png";
import ConvoAutomate from "../../../assets/images/convo-automate.png";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  role?: string;
  onNavigate?: (path: string) => void;
}

type SuiteId = "marketing" | "automate" | "crm" | "unify" | "track" | "cxo";

const ConvoHubPlatformModal: React.FC<Props> = ({
  isOpen,
  onClose,
  role,
}) => {
  const [showTour, setShowTour] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(1);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const navigate = useNavigate();

  const canAccessCrm = hasCrmAccess(role || "");
  const canAccessMarketing = hasMarketingAccess();

  const [activeSuite, setActiveSuite] = useState<SuiteId>(
    canAccessMarketing ? "marketing" : "crm"
  );

  // Updated: Use image strings instead of React components
  const SUITES: Array<{
    id: SuiteId;
    name: string;
    description: string;
    bullets: string;
    icon: string; // Changed to string
    disabled?: boolean;
    restrictedNote?: string;
  }> = [
      {
        id: "marketing",
        name: "Marketing",
        description: "Drive growth with campaigns & journeys Campaign Management, Journey Builder, A/B Testing, Audience Segmentation, Analytics",
        bullets: "Campaign Management, Journey Builder, A/B Testing, Audience Segmentation, Analytics",
        icon: ConvoMarketing,
        disabled: !canAccessMarketing,
        restrictedNote: "Your current role has access to ConvoCRM Hub only. Contact your company admin to enable ConvoMarketing access.",
      },
      {
        id: "automate",
        name: "Automate",
        description: "Automate any workflow, not just campaigns Workflow Builder, Trigger Library, AI Automation Nodes, Approval & Escalation Rules",
        bullets: "Workflow Builder, Trigger Library, AI Automation Nodes, Approval & Escalation Rules",
        icon: ConvoAutomate,
        disabled: !canAccessMarketing,
        restrictedNote: "Your current role has access to ConvoCRM Hub only. Contact your company admin to enable ConvoAutomate access.",
      },
      {
        id: "crm",
        name: "CRM",
        description: "Manage relationships & pipeline Unified Inbox, Contact Management, Sales Pipeline, Activity Tracking, Reporting",
        bullets: "Unified Inbox, Contact Management, Sales Pipeline, Activity Tracking, Reporting",
        icon: ConvoCrm,
        disabled: !canAccessCrm,
        restrictedNote: "Your current role has access to ConvoMarketing Hub only. Contact your company admin to enable ConvoCRM access.",
      },
      {
        id: "unify",
        name: "Unify",
        description: "Unify every customer signal (CDP) Identity Resolution, Unified Customer Profile, Data Connectors, Consent Management, Audience Sync",
        bullets: "Identity Resolution, Unified Customer Profile, Data Connectors, Consent Management, Audience Sync",
        icon: ConvoUnify,
      },
      {
        id: "track",
        name: "Track",
        description: "Capture events from every touchpoint Website/App SDKs, Event Schema, API Keys, Server-side Events, Debug Console",
        bullets: "Website/App SDKs, Event Schema, API Keys, Server-side Events, Debug Console",
        icon: ConvoTrack,
      },
      {
        id: "cxo",
        name: "CXO",
        description: "Cross-module KPIs, one dashboard, full navigation access.",
        bullets: "Opens as your home screen, with full access to all modules and cross-module KPIs.",
        icon: ConvoTrack,
      },
    ];

  const SUITE_ROUTES: Record<SuiteId, string> = {
    marketing: "/cpas-dashboard",
    automate: "/automate/bot-studio",
    crm: "/crm/dashboard",
    unify: "/unify/overview",
    track: "/track/overview",
    cxo: "/cxo/overview",
  };

  const tourSteps: TourStep[] = [
    {
      id: 1,
      title: "Welcome to Your CPaaS Platform",
      description:
        "Let me show you around your new unified communication platform",
      icon: <MessageSquare className="w-8 h-8 text-white" />,
      features: ["Unified dashboard", "Real-time insights", "Smart automation"],
    },
    {
      id: 2,
      title: "Marketing Automation",
      description:
        "Create targeted campaigns that convert with intelligent automation",
      icon: <TrendingUp className="w-8 h-8 text-white" />,
      features: ["Multi-channel campaigns", "Behavioral triggers"],
    },
    {
      id: 3,
      title: "CRM & Customer Management",
      description:
        "Manage relationships and track every interaction seamlessly",
      icon: <Users className="w-8 h-8 text-white" />,
      features: ["Contact management", "Sales pipeline"],
    },
    {
      id: 4,
      title: "Analytics & Insights",
      description:
        "Make data-driven decisions with comprehensive reporting",
      icon: <BarChart3 className="w-8 h-8 text-white" />,
      features: ["Real-time dashboards", "AI recommendations"],
    },
  ];

  const handleNextStep = () => {
    if (currentTourStep < tourSteps.length) {
      setCurrentTourStep((prev) => prev + 1);
    } else {
      setShowTour(false);
      setCurrentTourStep(1);
    }
  };

  const handlePreviousStep = () => {
    if (currentTourStep > 1) {
      setCurrentTourStep((prev) => prev - 1);
    }
  };

  const handleCloseTour = () => {
    setShowTour(false);
    setCurrentTourStep(1);
  };

  if (!isOpen) return null;

  const handleContinue = () => {
    const suite = SUITES.find((s) => s.id === activeSuite);
    if (suite?.disabled) return;

    setIsSettingUp(true);

    setTimeout(() => {
      navigate(SUITE_ROUTES[activeSuite]);
    }, 2000);
  };

  if (showTour) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="relative w-full max-w-4xl">
          <TourScreen
            step={tourSteps[currentTourStep - 1]}
            currentStep={currentTourStep}
            totalSteps={tourSteps.length}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
            onClose={handleCloseTour}
            assistantMessage="Let me guide you through the platform."
          />
        </div>
      </div>
    );
  }

  if (isSettingUp) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-10 text-center shadow-2xl w-[500px]">
          <h2 className="text-xl font-semibold mb-3">
            Setting things up...
          </h2>

          <p className="text-gray-600 mb-6">
            We're preparing your workspace. This won't take long.
          </p>

          <div className="flex justify-center">
            <div className="w-6 h-6 border-4 border-gray-300 border-t-[#211C64] rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 to-indigo-50 dark:from-gray-950 dark:to-gray-950 overflow-y-auto z-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
          Welcome to <span className="font-medium">ConvoHub</span>
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2">Reach customers on every channel, effortlessly.</p>

        <p className="lg:text-[16px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-8 mb-3">*select the suite you wanted to move forward with</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SUITES.map((suite) => {
            const selected = activeSuite === suite.id;
            return (
              <button
                key={suite.id}
                type="button"
                onClick={() => !suite.disabled && setActiveSuite(suite.id)}
                disabled={suite.disabled}
                title={suite.disabled ? suite.restrictedNote : undefined}
                className={`text-left rounded-2xl border-2 p-5 sm:p-6 transition-colors bg-white dark:bg-gray-900 ${suite.disabled
                  ? "opacity-50 cursor-not-allowed border-gray-100 dark:border-gray-800"
                  : selected
                    ? "border-indigo-600 dark:border-indigo-500"
                    : "border-gray-100 dark:border-gray-800 hover:border-indigo-200 dark:hover:border-indigo-800"
                  }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-full border-2 shrink-0 ${selected && !suite.disabled ? "bg-indigo-600 border-indigo-600" : "border-gray-300 dark:border-gray-600"
                      }`}
                  >
                    {selected && !suite.disabled && <Check size={12} className="text-white" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        Convo <span className="text-indigo-600 dark:text-indigo-400">{suite.name}</span>

                        <p className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 mt-1.5">{suite.description}</p>
                      </h3>
                      {/* Use img tag instead of Icon component */}
                      <img
                        src={suite.icon}
                        alt={`Convo ${suite.name}`}
                        className=" object-contain shrink-0"
                      />
                    </div>
                    {suite.disabled && (
                      <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 px-1.5 py-0.5 rounded">
                        Restricted
                      </span>
                    )}

                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleContinue}
          className="w-full mt-6 px-6 py-3.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default ConvoHubPlatformModal;